import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@heidi/design-system/button';
import { TypographyH3, TypographyP } from '@heidi/design-system/typography';
import { ScreenName } from '@heidi/runtime/analytics-vocabulary';

import { OnboardingStepShell } from '../shared/OnboardingStepShell';
import {
  FALLBACK_SAMPLE_TEXT,
  SIMULATED_TRANSCRIPTION_TOTAL_MS,
} from '../steps/hooks/useSimulatedTranscription';
import { tourStepsMessages } from './tour-steps.messages';
import { useTourScreenView } from './useTourScreenView';

interface TourTranscriptionStepProps {
  onContinue: () => void;
  onSkip: () => void;
  onTranscriptChange: (transcript: string) => void;
  onRecordingDurationChange?: (seconds: number) => void;
  /** Specialty-specific sample consult. Falls back to a generic script. */
  sampleText?: string;
  /** Specialty label rendered inside the heading (e.g. "GP"). */
  specialty?: string;
  /**
   * Gate the CTA until the sample has finished streaming. `false` once the
   * user has been through the step, so Back navigation doesn't re-block them.
   */
  requireRecordingToContinue?: boolean;
}

/**
 * Guided-tour screen 2: the mic-test step's treatment design. There is no
 * mic-permission prompt on the learn-basics flow, and the design drops the
 * play/record choice entirely — the sample consult streams into the preview on
 * entry and the single CTA unlocks once the transcript is complete.
 */
export function TourTranscriptionStep({
  onContinue,
  onSkip,
  onTranscriptChange,
  onRecordingDurationChange,
  sampleText,
  specialty,
  requireRecordingToContinue = true,
}: TourTranscriptionStepProps) {
  const [isTranscribed, setIsTranscribed] = useState(false);

  useTourScreenView(ScreenName.OnboardingTourTranscription);

  const sampleWords = useMemo(() => {
    const rawText = sampleText?.trim() ? sampleText : FALLBACK_SAMPLE_TEXT;
    return rawText.split(/\s+/).filter(Boolean);
  }, [sampleText]);

  // Stream the consult word by word so the right pane transcribes live, at the
  // same fixed total duration every specialty's sample uses in the control arm.
  useEffect(() => {
    if (sampleWords.length === 0) return;
    const intervalMs = SIMULATED_TRANSCRIPTION_TOTAL_MS / sampleWords.length;
    let revealed = 0;
    const id = setInterval(() => {
      revealed += 1;
      onTranscriptChange(sampleWords.slice(0, revealed).join(' '));
      if (revealed < sampleWords.length) return;
      clearInterval(id);
      onRecordingDurationChange?.(Math.round(SIMULATED_TRANSCRIPTION_TOTAL_MS / 1000));
      setIsTranscribed(true);
    }, intervalMs);
    return () => clearInterval(id);
  }, [sampleWords, onTranscriptChange, onRecordingDurationChange]);

  return (
    <OnboardingStepShell
      contentClassName="gap-6"
      footer={
        // Right-aligned row, matching `TourStepShell` and the design's
        // "Button Row".
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button type="button" size="lg" variant="ghost" onClick={onSkip}>
            <FormattedMessage {...tourStepsMessages.skip} />
          </Button>
          <Button
            type="button"
            size="lg"
            onClick={onContinue}
            disabled={requireRecordingToContinue && !isTranscribed}
          >
            <FormattedMessage {...tourStepsMessages.transcriptionCta} />
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-2">
        <TypographyH3>
          {specialty ? (
            <FormattedMessage
              {...tourStepsMessages.transcriptionTitle}
              values={{
                specialty: <span className="text-accent-forest">{specialty}</span>,
              }}
            />
          ) : (
            <FormattedMessage {...tourStepsMessages.transcriptionTitleGeneric} />
          )}
        </TypographyH3>
        <TypographyP className="text-text-secondary">
          <FormattedMessage {...tourStepsMessages.transcriptionSubtitle} />
        </TypographyP>
      </div>
    </OnboardingStepShell>
  );
}
